import { useEffect, useState } from 'react'
import notify from 'components/atoms/Notify'
import apiGet from 'lib/network/apiGet'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IoSearch } from 'react-icons/io5'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import Selection from 'components/molecules/Selection'

const getLocalizedLabel = (enValue, arValue, isArabic) => (isArabic ? arValue || enValue || '' : enValue || arValue || '')

const createOption = (id, enValue, arValue, isArabic) => ({
  id,
  enName: enValue || '',
  arName: arValue || '',
  name: getLocalizedLabel(enValue || '', arValue || '', isArabic),
})

const AddRentalCar = ({ bookingData, onSelectCar, onClose, loadingCarId, carActionInProgressId = null, selectedCarId = null, isAdmin = false }) => {
  const [carData, setCarData] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [companyNames, setCompanyNames] = useState([])
  const [selectedCompanies, setSelectedCompanies] = useState([])
  const [marketSegments, setMarketSegments] = useState([])
  const [selectedMarketSegments, setSelectedMarketSegments] = useState([])
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPage: 1,
    pageSize: 10,
  })
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'

  const relocalizeCompanyOptions = options =>
    options?.map(opt => ({
      ...opt,
      name: getLocalizedLabel(opt.enName ?? opt.name, opt.arName ?? opt.name, isArabic),
    })) ?? []

  const relocalizeMarketSegmentOptions = options =>
    options?.map(opt => ({
      ...opt,
      name: getLocalizedLabel(opt.enName ?? opt.name, opt.arName ?? opt.name, isArabic),
    })) ?? []

  const debouncedSearch = _.debounce((searchTerm, page, companyIds, marketSegmentIds, minDailyRate, maxDailyRate) => {
    fetchCars(page, searchTerm, companyIds, marketSegmentIds, minDailyRate, maxDailyRate)
  }, 300)
  const fetchCars = async (page = 1, searchTerm = '', companyIds = [], marketSegmentIds = [], minDailyRate = 0, maxDailyRate = 500) => {
    if (!bookingData.rentalStartDate || !bookingData.rentalEndDate) {
      setCarData([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const params = {
        'booking[from]': bookingData.rentalStartDate,
        'booking[to]': bookingData.rentalEndDate,
        'location[lat]': bookingData.lat,
        'location[lon]': bookingData.lon,
        isAdmin: isAdmin.toString(),
        pageNumber: String(page),
        pageSize: '9',
      }

      if (_.trim(searchTerm)) {
        params.query = _.trim(searchTerm)
      }
      const validCompanyIds = _.isArray(companyIds) ? _.filter(companyIds, id => id != null && id !== '') : []
      if (validCompanyIds.length > 0) {
        params['filters[companies]'] = validCompanyIds.join(',')
      }
      const validMarketSegmentIds = _.isArray(marketSegmentIds) ? _.filter(marketSegmentIds, id => id != null && id !== '') : []
      if (validMarketSegmentIds.length > 0) {
        params['filters[marketSegments]'] = validMarketSegmentIds.join(',')
      }
      // Sanitize and clamp price range so we never send invalid characters
      const sanitizeRate = value => {
        if (value === '' || value == null) return null
        const numeric = parseFloat(String(value).replace(/[^\d.]/g, ''))
        if (Number.isNaN(numeric)) return null
        return Math.min(500, Math.max(0, numeric))
      }

      const hasMinInput = minDailyRate !== '' && minDailyRate != null
      const hasMaxInput = maxDailyRate !== '' && maxDailyRate != null

      // If both fields are empty, don't send priceRange filter at all
      if (hasMinInput || hasMaxInput) {
        const minRate = sanitizeRate(minDailyRate)
        const maxRate = sanitizeRate(maxDailyRate)

        // When from changes, also send default to (500) if not provided, and vice versa
        const fromToSend = minRate != null ? minRate : 0
        const toToSend = maxRate != null ? maxRate : 500

        params['filters[priceRange][from]'] = String(fromToSend)
        params['filters[priceRange][to]'] = String(toToSend)
      }

      const response = await apiGet('cars/for-bookings', params)

      if (response.success || response.code === 200) {
        const { rentalCars, pagination: newPagination } = response.data

        setCarData(prevData => {
          return page === 1 ? rentalCars : [...prevData, ...rentalCars]
        })

        setPagination({
          currentPage: page,
          totalPage: newPagination.totalPage,
          pageSize: newPagination.pageSize,
        })
      }
    } catch (error) {
      notify.error('Failed to fetch cars')
    } finally {
      setLoading(false)
    }
  }

  const fetchCompanies = async () => {
    try {
      const response = await apiGet('companies', { pageSize: 1000 })
      if (response?.data?.company) {
        const companies = response.data.company.map(c => createOption(c._id, c.name, c.nameAr, isArabic))
        setCompanyNames(companies)
      } else {
        setCompanyNames([])
      }
    } catch {
      setCompanyNames([])
    }
  }

  const fetchMarketSegmentsList = async () => {
    try {
      const response = await apiGet('market-segments', { pageSize: 500, pageNumber: 1 })
      if (response?.data?.marketSegment) {
        const segments = response.data.marketSegment.map(item => createOption(item.marketSegment, item.marketSegment, item.marketSegmentAr, isArabic))
        setMarketSegments(segments)
      } else {
        setMarketSegments([])
      }
    } catch {
      setMarketSegments([])
    }
  }

  useEffect(() => {
    fetchCompanies()
    fetchMarketSegmentsList()
  }, [])

  useEffect(() => {
    setCompanyNames(prev => relocalizeCompanyOptions(prev))
  }, [i18n.language])

  useEffect(() => {
    setMarketSegments(prev => relocalizeMarketSegmentOptions(prev))
  }, [i18n.language])

  useEffect(() => {
    setCarData([])
    if (bookingData.rentalStartDate && bookingData.rentalEndDate) {
      debouncedSearch(search, 1, selectedCompanies, selectedMarketSegments, minPrice, maxPrice)
    }

    return () => debouncedSearch.cancel()
  }, [search, selectedCompanies, selectedMarketSegments, minPrice, maxPrice])

  useEffect(() => {
    setCarData([])
    if (bookingData.rentalStartDate && bookingData.rentalEndDate) {
      fetchCars(1, search, selectedCompanies, selectedMarketSegments, minPrice, maxPrice)
    }
  }, [bookingData.rentalStartDate, bookingData.rentalEndDate, bookingData.lat, bookingData.lon])

  const loadMore = () => {
    if (pagination.currentPage < pagination.totalPage && bookingData.rentalStartDate && bookingData.rentalEndDate) {
      fetchCars(pagination.currentPage + 1, search, selectedCompanies, selectedMarketSegments, minPrice, maxPrice)
    }
  }

  const handleMinPriceChange = e => {
    const { value } = e.target
    if (value === '') {
      setMinPrice('')
      return
    }
    const num = Number(value)
    if (Number.isNaN(num)) return
    const clamped = Math.min(500, Math.max(0, num))
    setMinPrice(clamped)
  }
  const handleMaxPriceChange = e => {
    const { value } = e.target
    if (value === '') {
      setMaxPrice('')
      return
    }
    const num = Number(value)
    if (Number.isNaN(num)) return
    const clamped = Math.min(500, Math.max(0, num))
    setMaxPrice(clamped)
  }

  const handleRentClick = carId => {
    if (carId) {
      onSelectCar(carId)
    }
  }

  const handleSearchChange = e => {
    const value = e.target.value
    setSearch(value)
  }

  return (
    <div
      className="flex flex-col h-full"
      id="scrollableDiv"
      style={{
        overflow: 'auto',
        height: 'calc(100vh - 200px)',
      }}
    >
      <div className="mb-4 px-4 space-y-4">
        {/* Row 1: Dropdown filters - equal width, consistent spacing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
          <div className="min-w-0">
            <label htmlFor="companies-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookingProfile.labels.companiesFilter')}
            </label>
            <Selection
              id="companies-filter"
              list={companyNames}
              title={t('bookingProfile.placeholders.selectCompanies')}
              value={selectedCompanies}
              onChange={newVal => {
                if (_.isObject(newVal) && newVal.id != null) {
                  setSelectedCompanies(prev => _.xor(prev || [], [newVal.id]))
                } else if (_.isArray(newVal)) {
                  setSelectedCompanies(newVal.map(x => (_.isObject(x) && x.id != null ? x.id : x)))
                }
              }}
              multiple
              className="w-full border border-gray-300 rounded-md"
            />
          </div>
          <div className="min-w-0">
            <label htmlFor="market-segment-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookingProfile.labels.marketSegmentFilter')}
            </label>
            <Selection
              id="market-segment-filter"
              list={marketSegments}
              title={t('bookingProfile.placeholders.selectMarketSegment')}
              value={selectedMarketSegments}
              onChange={newVal => {
                if (_.isObject(newVal) && newVal.id != null) {
                  setSelectedMarketSegments(prev => _.xor(prev || [], [newVal.id]))
                } else if (_.isArray(newVal)) {
                  setSelectedMarketSegments(newVal.map(x => (_.isObject(x) && x.id != null ? x.id : x)))
                }
              }}
              multiple
              className="w-full border border-gray-300 rounded-md"
            />
          </div>
          <div className="min-w-0 relative">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookingProfile.placeholders.searchOptions')}
            </label>
            <div className="relative rounded-md border border-gray-300 bg-gray-50">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearch className="h-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                id="search"
                onChange={handleSearchChange}
                value={search}
                type="text"
                name="search"
                className="block pl-9 pr-3 py-2 w-full border-0 bg-transparent text-sm placeholder-gray-500 focus:outline-none focus:ring-0"
                placeholder={t('bookingProfile.placeholders.searchOptions')}
              />
            </div>
          </div>
        </div>

        {/* Row 2: Price range inputs (min/max) */}
        <div className="w-full max-w-xl">
          <p className="block text-sm font-medium text-gray-700 mb-2">{t('bookingProfile.labels.priceRange')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="min-price" className="block text-xs font-medium text-gray-600 mb-1">
                {t('bookingProfile.labels.minPrice')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="min-price"
                  type="number"
                  min={0}
                  max={500}
                  step={1}
                  value={minPrice}
                  onChange={handleMinPriceChange}
                  placeholder="0"
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500"
                />
                {/* <span className="text-xs text-gray-500 whitespace-nowrap">{t('common.omr')}/day</span> */}
              </div>
            </div>
            <div>
              <label htmlFor="max-price" className="block text-xs font-medium text-gray-600 mb-1">
                {t('bookingProfile.labels.maxPrice')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="max-price"
                  type="number"
                  min={0}
                  max={500}
                  step={1}
                  value={maxPrice}
                  onChange={handleMaxPriceChange}
                  placeholder="500"
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-dark-500 focus:border-dark-500"
                />
                {/* <span className="text-xs text-gray-500 whitespace-nowrap">{t('common.omr')}/day</span> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow px-4">
        {loading && carData.length === 0 ? (
          <div className="w-full h-[200px] border-0 flex justify-center items-center">
            <LoadingIcon />
          </div>
        ) : !bookingData.rentalStartDate || !bookingData.rentalEndDate ? (
          <EmptyState v2 description="Please set rental start and end dates to view available cars" />
        ) : carData.length === 0 ? (
          <EmptyState v2 description="No cars available for the selected dates" />
        ) : (
          <InfiniteScroll
            dataLength={carData.length}
            next={loadMore}
            hasMore={pagination.currentPage < pagination.totalPage}
            loader={
              <div className="w-full h-16 flex justify-center items-center">
                <LoadingIcon />
              </div>
            }
            scrollThreshold={0.8}
            scrollableTarget="scrollableDiv"
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {carData.map(rentalCarData => (
                <div key={rentalCarData._id} className="bg-white rounded-lg shadow-md overflow-hidden p-4 relative">
                  <div className="relative">
                    <img src={rentalCarData.carImages[0].url} alt={'RentalCar'} className="w-full h-40 object-cover rounded-md mb-3" />
                    <img
                      src={rentalCarData.company.logoUrl}
                      alt="Company Logo"
                      className="absolute top-2 right-2 w-16 h-16 object-cover rounded-md border border-white shadow-md"
                    />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {rentalCarData.car.make} {rentalCarData.car.model} ({rentalCarData.modelYear})
                  </h3>
                  <p className="text-sm text-gray-500">
                    {rentalCarData.car.marketSegment} | {rentalCarData.car.numberOfSeats} |{rentalCarData.car.shifter || ''} |{' '}
                    {rentalCarData.unlimitedKm || !rentalCarData.allowedKMPerDay ? 'Unlimited' : `${rentalCarData.allowedKMPerDay} km/day`}
                  </p>
                  <p className="text-sm text-gray-500">Distance: {rentalCarData.distance}</p>
                  {rentalCarData.isDeliveryAvailable && <p className="text-sm text-green-500"> Available for delivery</p>}
                  <p className="text-lg font-bold text-black-600 mt-2">{rentalCarData.dailyRate} OMR/day</p>
                  <button
                    type="button"
                    onClick={() => handleRentClick(rentalCarData)}
                    disabled={carActionInProgressId === rentalCarData._id || loadingCarId === rentalCarData._id}
                    className="w-full bg-dark-600 text-white py-2 px-4 rounded-lg hover:bg-dark-700 transition-colors mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingCarId === rentalCarData._id || carActionInProgressId === rentalCarData._id
                      ? t('updateBooking.changeCarWizard.processing')
                      : selectedCarId === rentalCarData._id
                      ? t('updateBooking.changeCarWizard.added')
                      : t('updateBooking.changeCarWizard.addThisCar')}
                  </button>
                </div>
              ))}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  )
}

export default AddRentalCar
